import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Edit2, Printer, Trash2, DollarSign, CheckCircle, Package, Truck, Layers, AlertCircle, AlertTriangle, Factory, ShoppingCart, Image, PlayCircle, ExternalLink, ArrowRightLeft } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800', pending_bom: 'bg-orange-100 text-orange-800',
  pending_variant_selection: 'bg-amber-100 text-amber-800', pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800', in_production: 'bg-purple-100 text-purple-800',
  partially_dispatched: 'bg-teal-100 text-teal-800', completed: 'bg-green-100 text-green-800',
  dispatched: 'bg-green-200 text-green-900', cancelled: 'bg-red-100 text-red-800'
};
const statusLabels = {
  draft: 'Draft', pending_bom: 'Pending BOM', pending_variant_selection: 'Pending Variants',
  pending: 'Pending', confirmed: 'Confirmed', in_production: 'In Production',
  partially_dispatched: 'Partial Dispatch', completed: 'Completed', dispatched: 'Dispatched', cancelled: 'Cancelled'
};

const dispatchStatusColors = {
  pending: 'bg-gray-100 text-gray-800',
  partially_dispatched: 'bg-amber-100 text-amber-800',
  fully_dispatched: 'bg-green-100 text-green-800'
};

export default function SODetailView({
  order,
  client,
  workOrders,
  woDeliveryStatus,
  canSeePrices,
  canEditSO,
  canDeleteSO,
  onBack,
  onSelectLineItem,
  onEdit,
  onPrint,
  onOpenPayment,
  onConfirmSO,
  onDeleteLineItem,
  hasItemBOM,
  onChangeItem,
}) {
  const navigate = useNavigate();
  
  if (!order) return null;

  const lineItems = order.line_items || [];
  const totalDispatched = lineItems.reduce((s, li) => s + (li.dispatched_quantity || 0), 0);
  const totalQty = lineItems.reduce((s, li) => s + (li.quantity || 0), 0);
  const dispatchPct = totalQty > 0 ? Math.round((totalDispatched / totalQty) * 100) : 0;
  const deliveryData = woDeliveryStatus[order.id];

  // Payment info
  const totalAmount = order.total_amount || 0;
  const totalPaid = order.total_advance || 0;
  const balanceDue = order.balance_due || (totalAmount - totalPaid) || 0;

  // Delivery address
  const deliveryAddr = client?.delivery_addresses?.find(a => (a.id || a.city_code) === order.delivery_address_id);

  return (
    <div className="space-y-5">
      {/* Back + Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-0.5 shrink-0" data-testid="so-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-semibold tracking-tight">{order.so_number}</h2>
            <Badge className={statusColors[order.status]}>{statusLabels[order.status] || order.status}</Badge>
            {deliveryData && deliveryData.total_wos > 0 && (() => {
              const cfg = {
                'ON_TRACK': { l: 'On Track', c: 'bg-green-100 text-green-700' },
                'AT_RISK': { l: 'At Risk', c: 'bg-yellow-100 text-yellow-700' },
                'DELAYED': { l: 'Delayed', c: 'bg-red-100 text-red-700' }
              }[deliveryData.overall_status];
              return cfg ? <Badge variant="outline" className={`text-xs ${cfg.c}`}>{cfg.l}</Badge> : null;
            })()}
          </div>
          <p className="text-base font-medium text-muted-foreground mt-0.5">{client?.name || order.customer_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canSeePrices && (
            <Button size="sm" variant="outline" onClick={() => onOpenPayment(order)}>
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Payment
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onPrint(order)}>
            <Printer className="w-3.5 h-3.5 mr-1" /> Print
          </Button>
          {canEditSO && order.status !== 'completed' && (
            <Button size="sm" variant="outline" onClick={() => onEdit(order)}>
              <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Client</p>
          <p className="text-sm font-semibold mt-0.5">{client?.name || order.customer_name}</p>
          {client?.gst_number && <p className="text-xs text-muted-foreground">GST: {client.gst_number}</p>}
        </div>
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Delivery</p>
          <p className="text-sm font-semibold mt-0.5">{formatDate(order.delivery_date)}</p>
          {deliveryAddr && <p className="text-xs text-muted-foreground">{deliveryAddr.city_code || deliveryAddr.city}</p>}
          {order.target_production_date && <p className="text-xs text-muted-foreground">Prod: {formatDate(order.target_production_date)}</p>}
        </div>
        {canSeePrices && (
          <div className="bg-muted/30 rounded-lg px-4 py-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Payment</p>
            <p className="text-sm font-semibold mt-0.5">₹{totalAmount.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">
              Paid: <span className="text-emerald-600">₹{totalPaid.toLocaleString()}</span>
              {balanceDue > 0 && <> | Due: <span className="text-orange-600">₹{balanceDue.toLocaleString()}</span></>}
            </p>
          </div>
        )}
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Dispatch</p>
          <p className="text-sm font-semibold mt-0.5">{totalDispatched} / {totalQty} dispatched</p>
          <Progress value={dispatchPct} className="h-1.5 mt-1" />
        </div>
      </div>

      {/* Line Items Table */}
      <Card className="border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-10"></TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-center w-[80px]">Qty</TableHead>
                <TableHead className="w-[80px]">Type</TableHead>
                <TableHead className="w-[130px]">WO</TableHead>
                <TableHead className="w-[150px]">Dispatch</TableHead>
                {canSeePrices && <TableHead className="text-right w-[90px]">Amount</TableHead>}
                <TableHead className="w-[100px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((li, idx) => {
                const hasBOM = hasItemBOM(li.item_id);
                const existingWO = workOrders.find(wo => wo.sales_order_id === order.id && (wo.line_item_id === li.id || wo.item_id === li.item_id));
                const woAllocs = li.wo_allocations || [];
                const dispatched = li.dispatched_quantity || 0;
                const liDispatchPct = li.quantity > 0 ? Math.round((dispatched / li.quantity) * 100) : 0;
                const fulfillment = li.fulfillment_type || (li.item_source_type === 'IP' ? 'Make' : 'Buy');
                const needsAttention = li.has_bom === false || li.needs_variant_selection;

                return (
                  <TableRow
                    key={li.id || idx}
                    className={`cursor-pointer transition-colors hover:bg-accent/50 ${needsAttention ? 'bg-orange-50/30' : ''}`}
                    onClick={() => onSelectLineItem(li.id)}
                    data-testid={`li-row-${idx}`}
                  >
                    <TableCell>
                      {li.item_image ? (
                        <img src={li.item_image} alt="" className="w-8 h-8 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-8 h-8 bg-muted rounded border flex items-center justify-center">
                          <Image className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium truncate">{li.item_name}</span>
                          {canEditSO && onChangeItem && order.status !== 'dispatched' && order.status !== 'completed' && order.status !== 'cancelled' && (
                            <Button
                              variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-40 hover:opacity-100"
                              data-testid={`change-item-${idx}`}
                              title="Change Item"
                              onClick={(e) => { e.stopPropagation(); onChangeItem(li); }}
                            >
                              <ArrowRightLeft className="w-3 h-3 text-blue-600" />
                            </Button>
                          )}
                          {hasBOM && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700">BOM</Badge>}
                          {li.variant_selections?.length > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-50 text-purple-700">
                              <Layers className="w-2.5 h-2.5 mr-0.5" />{li.variant_selections.length}
                            </Badge>
                          )}
                          {li.is_fulfillment_override && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-50 text-amber-700">Override</Badge>
                          )}
                        </div>
                        {/* SO Line Item Number */}
                        {li.item_code && (
                          <p className="text-[10px] font-mono text-blue-600 mt-0.5">{li.item_code}</p>
                        )}
                        {li.variant_code && li.variant_code !== li.item_name?.toUpperCase() && (
                          <p className="text-[10px] font-mono text-purple-600 mt-0.5">{li.variant_code}</p>
                        )}
                        {li.has_bom === false && <Badge className="text-[10px] bg-orange-100 text-orange-800 mt-0.5"><AlertCircle className="w-2.5 h-2.5 mr-0.5" />No BOM</Badge>}
                        {li.needs_variant_selection && <Badge className="text-[10px] bg-amber-100 text-amber-800 mt-0.5"><Layers className="w-2.5 h-2.5 mr-0.5" />Needs Variants</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-sm">{li.quantity}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${fulfillment === 'Make' ? 'bg-orange-50 text-orange-700' : 'bg-purple-50 text-purple-700'}`}>
                        {fulfillment === 'Make' ? <Factory className="w-3 h-3 mr-0.5" /> : <ShoppingCart className="w-3 h-3 mr-0.5" />}
                        {fulfillment}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {woAllocs.length > 0 ? (
                        <div className="space-y-0.5">
                          {woAllocs.map((alloc, ai) => (
                            <button
                              key={ai}
                              className="text-xs text-blue-600 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/production/work-orders/view/${alloc.work_order_id}`);
                              }}
                            >
                              {alloc.work_order_number}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          ))}
                        </div>
                      ) : existingWO ? (
                        <button
                          className="text-xs text-blue-600 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/production/work-orders/view/${existingWO.id}`);
                          }}
                        >
                          {existingWO.work_order_number}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      ) : fulfillment === 'Make' ? (
                        <span className="text-xs text-orange-600">Pending WO</span>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      {li.quantity > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Progress value={liDispatchPct} className="h-1.5 flex-1" />
                            <span className="text-xs text-muted-foreground w-8 text-right">{liDispatchPct}%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{dispatched}/{li.quantity}</p>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    {canSeePrices && (
                      <TableCell className="text-right">
                        <span className="font-semibold text-sm">₹{(li.amount || 0).toLocaleString()}</span>
                      </TableCell>
                    )}
                    <TableCell>
                      {li.dispatch_status === 'fully_dispatched' ? (
                        <Badge className="text-xs bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-0.5" />Done</Badge>
                      ) : li.dispatch_status === 'partially_dispatched' ? (
                        <Badge className="text-xs bg-amber-100 text-amber-800"><Package className="w-3 h-3 mr-0.5" />Partial</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Pending</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {lineItems.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No line items in this order</p>
            </div>
          )}
        </CardContent>
      </Card>
      {canSeePrices && lineItems.length > 0 && (
        <p className="text-sm text-right font-semibold">
          Total: ₹{(order.total_amount || 0).toLocaleString()} &middot; {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
